import { Mutation, Query } from '@tanstack/react-query';
import NOTIFICATION_API from 'constants/notification/api';
import { isObject } from 'lodash-es';
import { logout } from 'services/AuthService/Login/actions';
import store from 'store';
import type { OneServiceMeta } from 'types/services/services';
import OneBadRequest from 'utils/common/errors/OneBadRequest';
import OneBusinessError from 'utils/common/errors/OneBusinessError';
import OneUnauthorizedError from 'utils/common/errors/OneUnauthorizedError';
import triggerNotification from 'utils/event/triggerNotification';
import manageError from 'utils/logging/manageError';

/**
 * Custom error handler to skip errors specific to the ONE.
 * @param error the error from API call
 * @returns true if the error should be skipped, false otherwise
 */
const skipError = (error: unknown) => {
  if (error instanceof OneBusinessError || error instanceof OneBadRequest) {
    return true;
  }

  if (isObject(error)) {
    if ('errorCode' in error) {
      // skip in case of 10004 error code
      return `${error.errorCode}` === '10004';
    }
  }

  return false;
};

/**
 * Build extra data for the error notification.
 * @param source query or mutation source of api call
 * @returns extra data for the error notification
 */
const buildExtra = (source: Query | Mutation<unknown, unknown, unknown, unknown>) => {
  if ('queryKey' in source) {
    const { queryHash, queryKey, state, options, meta, cacheTime } = source;

    return JSON.stringify({ queryHash, queryKey, state, options, meta, cacheTime });
  }

  const { meta, mutationId, options, state, cacheTime } = source;

  return JSON.stringify({ meta, mutationId, options, state, cacheTime });
};

/**
 * Handle an error from an API call by triggering notification and logging.
 * @param error the error from API call
 * @param meta meta data from the API
 * @param source query or mutation source of api call
 */
const onApiError = (error: unknown, meta: OneServiceMeta | undefined, source: Query | Mutation<unknown, unknown, unknown, unknown>) => {
  if (!meta?.skipErrors) {
    // handle errors

    if (error instanceof OneUnauthorizedError) {
      // manage un authorized error
      // logout user
      logout(store.dispatch)();

      // notification
      triggerNotification({ id: NOTIFICATION_API.unAuthorizedError.id });
    } else if (!skipError(error)) {
      // manage generic error
      const notificationId = meta?.notifications?.error?.id ?? NOTIFICATION_API.unknownError.id;

      const sourceExtra = buildExtra(source);

      triggerNotification({ id: notificationId });
      manageError(error, { source: sourceExtra });
    }
  }
};

export default onApiError;
